import {useInfiniteData} from '@common/ui/infinite-scroll/use-infinite-data';
import {Track} from '@app/web-player/tracks/track';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {IllustratedMessage} from '@ui/images/illustrated-message';
import {AudiotrackIcon} from '@ui/icons/material/Audiotrack';
import {Trans} from '@ui/i18n/trans';
import React, {useState} from 'react';
import {Link, useParams} from 'react-router';
import {Album} from '@app/web-player/albums/album';
import {AlbumIcon} from '@ui/icons/material/Album';
import {Tabs} from '@ui/tabs/tabs';
import {TabList} from '@ui/tabs/tab-list';
import {Tab} from '@ui/tabs/tab';
import {TabPanel, TabPanels} from '@ui/tabs/tab-panels';

import {AlbumList} from '@app/web-player/albums/album-list/album-list';
import {TrackTable} from '@app/web-player/tracks/track-table/track-table';
import {VirtualTableBody} from '@app/web-player/playlists/virtual-table-body';
import {TableDataItem} from '@common/ui/tables/types/table-data-item';

import { useTagFollow } from '@player/custom/hooks/use-tag-follow.ts';
import { FavoriteIcon } from '@ui/icons/material/Favorite.tsx';

import { FavoriteBorderIcon } from '@ui/icons/material/FavoriteBorder.tsx';
import { useNormalizedModel } from '@common/ui/normalized-model/use-normalized-model.ts';

import { Button } from '@ui/buttons/button.tsx';
import { Loader2Icon } from 'lucide-react'

const tagTabNames = {
    tracks: 0,
    albums: 1,
};

export function TagMediaPage() {
    const params = useParams();
    const tagName = params.tagName!;
    const tabName = params['*']?.split('/').pop() || tagTabNames.tracks;
    const [selectedTab, setSelectedTab] = useState(
        tagTabNames[tabName as keyof typeof tagTabNames] || 0,
    );

    const query = useNormalizedModel(`normalized-model/tag/${tagName}`);

    return (
        <div>
            <h1 className="mb-40 flex items-center gap-10 text-xl md:text-3xl">
                {tabName === 'albums' ? (
                    <Trans
                        message="Most popular albums for #:tag"
                        values={{tag: tagName}}
                    />
                ) : (
                    <Trans
                        message="Most popular tracks for #:tag"
                        values={{tag: tagName}}
                    />
                )}

                {query?.data?.model && !query.isLoading && (
                    <FollowButton tag={query.data.model.id as number} />
                )}
            </h1>

            <Tabs selectedTab={selectedTab} onTabChange={setSelectedTab}>
                <TabList>
                    <Tab elementType={Link} to={`/tag/${tagName}`}>
                        <Trans message="Tracks" />
                    </Tab>
                    <Tab elementType={Link} to={`/tag/${tagName}/albums`}>
                        <Trans message="Albums" />
                    </Tab>
                </TabList>
                <TabPanels className="pt-24">
                    <TabPanel>
                        <TracksPanel tagName={tagName!} />
                    </TabPanel>
                    <TabPanel>
                        <AlbumsPanel tagName={tagName!} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
    );
}

function FollowButton({ tag }: { tag: number }) {
    const { isLiked, isLoading, toggleLike } = useTagFollow(tag)

    return (
        <Button
            startIcon={isLoading ? <Loader2Icon className="w-24 h-24 animate-spin" /> : isLiked ? <FavoriteIcon size="w-24 h-24" className="text-primary" /> : <FavoriteBorderIcon size="w-24 h-24" />}
            variant="outline"
            radius="xl"
            className="ml-auto rounded-md"
            onClick={() => toggleLike()}
            disabled={isLoading}
        >
            <Trans message={isLiked ? 'Unfollow' : 'Follow'} />
        </Button>
    )
}

interface TracksPanelProps {
    tagName: string;
}
function AlbumsPanel({tagName}: TracksPanelProps) {
    const query = useInfiniteData<Album>({
        queryKey: ['albums', 'tags', tagName],
        endpoint: `tags/${tagName}/albums`,
    });

    if (query.isLoading) {
        return <FullPageLoader className="min-h-100" screen={false} />;
    }

    if (!query.items.length) {
        return (
            <IllustratedMessage
                imageHeight="h-auto"
                imageMargin="mb-14"
                image={<AlbumIcon size="lg" className="text-muted" />}
                title={<Trans message="No albums yet" />}
                description={
                    <Trans message="This tag is not attached to any albums yet, check back later." />
                }
            />
        );
    }

    return <AlbumList query={query} />;
}

function TracksPanel({tagName}: TracksPanelProps) {
    const query = useInfiniteData<Track>({
        queryKey: ['tracks', 'tags', tagName],
        endpoint: `tags/${tagName}/tracks`,
        paginate: 'simple',
    });

    if (!query.isLoading && !query.items.length) {
        return (
            <IllustratedMessage
                imageHeight="h-auto"
                imageMargin="mb-14"
                image={<AudiotrackIcon size="lg" className="text-muted" />}
                title={<Trans message="No tracks yet" />}
                description={
                    <Trans message="This tag is not attached to any tracks yet, check back later." />
                }
            />
        );
    }

    return (
        <TrackTable
            tracks={query.isLoading ? getPlaceholderItems() : query?.items || []}
            // @ts-ignore
            query={query}
            enableSorting={false}
            tableBody={<VirtualTableBody query={query} totalItems={30} />}
        />
    );
}

function getPlaceholderItems(): TableDataItem[] {
    // 30 tracks per page by default
    return [...new Array(30).keys()].map(key => {
        return {
            isPlaceholder: true,
            id: `placeholder-${key}`,
        };
    });
}
